import { companyFieldHintForQuery, findDomainInQuery, TAB_COMPANY, TAB_PHONE } from '../pages/home/homeSearchUtils';
import { inferCompanyCountryFromDomain, parseCorporateHeroQuery } from '../pages/home/companyQueryParse';
import { QUERY_GUIDANCE_MESSAGE, validateInvestigationQuery } from './queryValidator';
import {
  formatBreachExposureLabel,
  resolveHibpBreachCount,
} from './hibpBreachCount';

export const STREAM_CHAT_ID = '';
export const TAB_PERSON = 'person';
export { TAB_PHONE, TAB_EMAIL, TAB_COMPANY } from '../pages/home/homeSearchUtils';

export function reviewResultKey(r) {
  if (!r || typeof r !== 'object') return '';
  return [r.url, r.source, r.title, r.type, r.platform, r.ui_bucket].map((x) => String(x ?? '')).join('\u0001');
}

/** SSE rows that are not logs, cache meta, or terminal scan status markers. */
export function filterStreamIntelRows(results) {
  const isSystemMsg = (res) =>
    !res?.ui_bucket &&
    (res?.status === 'starting' || res?.status === 'completed' || res?.status === 'cancelled');
  return (results || []).filter(
    (r) => r && r.type !== 'log' && r.type !== 'cache_meta' && !isSystemMsg(r),
  );
}

export function countStreamIntelRows(results) {
  return filterStreamIntelRows(results).length;
}

/**
 * Status label for the workspace scan bar.
 *
 * @param {{ loading?: boolean, completed?: boolean, scanFinished?: boolean, finalReport?: boolean, elapsedMs?: number }} opts
 * @returns {string}
 */
export function formatWorkspaceScanStatus({
  loading = false,
  completed = false,
  scanFinished = false,
  finalReport = false,
  elapsedMs = 0,
} = {}) {
  if (loading) return 'SCANNING IN PROGRESS…';
  if (completed || scanFinished || finalReport) {
    const sec = Math.max(0, Number(elapsedMs) || 0) / 1000;
    const runtime = Number.isFinite(sec) ? sec.toFixed(1) : '0.0';
    return `SCAN COMPLETED (${runtime}s)`;
  }
  return 'STANDBY';
}

/**
 * During an active scan, prefer live SSE hook rows; after completion use tab storage.
 * When a scan finishes with zero intel, never fall back to stale stored rows.
 */
export function pickIntelRowsForDisplay({ loading, completed, streamResults, storedResults }) {
  const stream = filterStreamIntelRows(streamResults);
  const stored = (storedResults || []).filter((r) => r?.type !== 'cache_meta');
  if (loading) return stream;
  if (completed && stream.length === 0) return [];
  return stored.length ? stored : stream;
}

export function filterStreamLogRows(results) {
  return (results || []).filter((r) => r?.type === 'log');
}

/** Person Search: exclude Censys infrastructure pivot rows from feed and scoring. */
export function isPersonCensysResult(r) {
  if (!r || typeof r !== 'object') return false;
  if (r.wiw_technical_pivot) return true;
  const platform = String(r.platform || '').toLowerCase();
  const source = String(r.source || '').toLowerCase();
  if (platform === 'censys') return true;
  return source === 'censys_pivot' || source.includes('censys');
}

export function validatePhoneInputValue(value) {
  const raw = String(value || '').trim();
  const allowAllToken = /(?:^|:)\s*all\s*(?:$|:)/i.test(raw);
  const stripped = allowAllToken ? raw.replace(/(?:^|:)\s*all\s*(?:$|:)/gi, '') : raw;
  const cleanValue = stripped.replace(/[\s-]/g, '');
  if (/[a-zA-Z]/.test(cleanValue)) return 'Phone number cannot contain letters.';
  const digitsOnly = cleanValue.replace('+', '');
  if (digitsOnly.length > 0 && (digitsOnly.length < 9 || digitsOnly.length > 15)) return 'Invalid phone length.';
  return '';
}

export function validateTabInputValue(tab, value) {
  if (tab === TAB_PHONE) return validatePhoneInputValue(value);
  const v = validateInvestigationQuery(value, tab);
  if (!v.isValid && v.reason && v.reason !== 'EMPTY') {
    return v.message || QUERY_GUIDANCE_MESSAGE;
  }
  return '';
}

export function normalizeCompanyDomainInput(val) {
  const found = findDomainInQuery(val);
  if (found) return found;
  return val.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
}

const DISPLAY_NAME_BY_UI_BUCKET = {
  relationship_node: (r) => `${r.target_entity} (${r.relation_type})`,
  compliance_alert: () => 'Sanctions Compliance Checked',
  cyber_trust_score: (r) => `Infrastructure Audit: ${r.domain}`,
  threat_intelligence: (r) => `Threat Intel: ${r.domain}`,
  wayback_archive: (r) => {
    try {
      const host = r.target_url ? new URL(r.target_url).hostname : '';
      return host ? `Wayback Archive: ${host}` : 'Wayback Machine Archive';
    } catch {
      return r.title || 'Wayback Machine Archive';
    }
  },
  email_deliverability: (r) => `Email Deliverability: ${r.email || r.domain || 'mailbox'}`,
  registered_service: (r) => {
    const name = r.service_name || r.platform || 'Registered Service';
    const family = String(r.account_family || '').trim();
    return family ? `${name} (${family})` : name;
  },
  ghunt_footprint: (r) =>
    r.details?.name ? `Google Footprint: ${r.details.name}` : `Google Footprint: ${r.gaia_id || 'Gaia'}`,
  sentiment_analysis: () => 'Employee & Consumer Reviews',
  ubo_network: () => 'Corporate Connections (UBO)',
  corporate_press: () => 'Corporate Press & Mentions',
  corporate_context: () => 'Corporate Search Context',
};

function displayNameFromSource(res) {
  if (res.source === 'ghunt' && res.platform === 'Google Account Info') return 'Verified Google Identity';
  if (res.source === 'hibp') return res.details?.title || res.details?.name || res.platform || 'Have I Been Pwned - breach';
  if (res.source === 'hibp_status' && res.status === 'clean')
    return res.message || 'No breach has been detected on this number';
  if (res.source === 'hibp_status' && res.status === 'breached')
    return res.message || 'Breach signals detected (HIBP)';
  if (res.source === 'hibp_status') return res.message || 'HIBP status';
  if (res.source === 'phone_metadata') return 'Phone Risk Summary';
  if (res.type === 'phone_identity')
    return res.details?.owner_name || res.details?.raw_data?.data?.name || 'NOT FOUND / UNCLAIMED';
  if (res.type === 'phone_mention') return res.title?.trim() || res.url || 'Web mention (dork)';
  return null;
}

export function getDisplayNameForResult(res) {
  try {
    const byBucket = DISPLAY_NAME_BY_UI_BUCKET[res.ui_bucket];
    if (byBucket) return byBucket(res);
    const bySource = displayNameFromSource(res);
    if (bySource != null) return bySource;
    if (res.title) return res.title;
    return res.platform || res.source_display_name || res.source || res.ui_bucket || 'Intelligence Record';
  } catch {
    return 'Processing Record...';
  }
}

function breachCountForStats(realResults) {
  return resolveHibpBreachCount(realResults);
}

function statsResultBand(currentScore, realResults, penalties) {
  const breaches = breachCountForStats(realResults);
  const count = realResults.length;
  if (currentScore >= 95) {
    return {
      score: currentScore,
      label: 'HIGH TRUST',
      colorClass: 'border-emerald-500 text-emerald-600',
      breaches,
      count,
      penalties,
    };
  }
  if (currentScore >= 70) {
    return {
      score: currentScore,
      label: 'MEDIUM RISK WARNING',
      colorClass: 'border-amber-500 text-amber-600',
      breaches,
      count,
      penalties,
    };
  }
  return {
    score: currentScore,
    label: 'CRITICAL RISK',
    colorClass: 'border-red-500 text-red-600 font-black',
    breaches,
    count,
    penalties,
  };
}

function computeCompanyTabScore(realResults) {
  let currentScore = 100;
  const penalties = [];
  const companyScoreData = realResults.find((r) => r.ui_bucket === 'cyber_trust_score');
  if (companyScoreData) {
    currentScore -= companyScoreData.risk_score;
    if (!companyScoreData.dmarc_secure) penalties.push({ reason: 'Unprotected Domain (No DMARC)', points: 25 });
    if (companyScoreData.age_days < 180) penalties.push({ reason: 'Recent Domain Creation', points: 40 });
  }
  const threatData = realResults.find((r) => r.ui_bucket === 'threat_intelligence');
  if (threatData && threatData.malicious > 0) {
    currentScore -= 40;
    penalties.push({ reason: 'Malicious Domain Flags', points: 40 });
  }
  const complianceData = realResults.find((r) => r.ui_bucket === 'compliance_alert');
  const complianceRisk = String(complianceData?.risk_level || '').toUpperCase();
  if (complianceData && ['PEP', 'SANCTIONED', 'WANTED', 'CRITICAL'].includes(complianceRisk)) {
    const reason =
      complianceRisk === 'PEP'
        ? 'PEP Exposure Match'
        : complianceRisk === 'WANTED'
          ? 'Wanted Entity Match'
          : 'Critical Sanctions Match';
    const points = complianceRisk === 'PEP' ? 35 : complianceRisk === 'WANTED' ? 45 : 50;
    currentScore -= points;
    penalties.push({ reason, points });
  }
  return { currentScore, penalties };
}

function computeNonCompanyTabScore(realResults) {
  let currentScore = 100;
  const penalties = [];
  const breachCount = resolveHibpBreachCount(realResults);
  if (breachCount > 0) {
    const bp = breachCount * 30;
    currentScore -= bp;
    const reason = formatBreachExposureLabel(breachCount);
    if (reason) penalties.push({ reason, points: bp });
  }
  const dmarcIssue = realResults.find(
    (r) => r.source === 'domain_intel' && r.matched_tags?.some((tag) => tag.includes('Vulnerable') || tag.includes('Missing')),
  );
  if (dmarcIssue) {
    currentScore -= 20;
    penalties.push({ reason: 'Vulnerable DMARC Policy', points: 20 });
  }
  const companyScoreData = realResults.find((r) => r.ui_bucket === 'cyber_trust_score');
  if (companyScoreData) {
    if (!companyScoreData.dmarc_secure) {
      currentScore -= 25;
      penalties.push({ reason: 'Unprotected Domain (No DMARC)', points: 25 });
    }
    if (companyScoreData.age_days < 180) {
      currentScore -= 40;
      penalties.push({ reason: 'Recent Domain Creation', points: 40 });
    }
  }
  const threatData = realResults.find((r) => r.ui_bucket === 'threat_intelligence');
  if (threatData && threatData.malicious > 0) {
    currentScore -= 40;
    penalties.push({ reason: 'Malicious Domain Flags', points: 40 });
  }
  const censysPivotPts = realResults
    .filter((r) => r.wiw_technical_pivot && typeof r.censys_risk_penalty === 'number')
    .reduce((sum, r) => sum + Math.min(28, Math.max(0, Number(r.censys_risk_penalty))), 0);
  if (censysPivotPts > 0) {
    const apply = Math.min(30, censysPivotPts);
    currentScore -= apply;
    penalties.push({ reason: 'Infrastructure exposure (Censys pivot)', points: apply });
  }
  const complianceData = realResults.find((r) => r.ui_bucket === 'compliance_alert');
  const complianceRisk = String(complianceData?.risk_level || '').toUpperCase();
  if (complianceData && ['PEP', 'SANCTIONED', 'WANTED', 'CRITICAL'].includes(complianceRisk)) {
    const reason =
      complianceRisk === 'PEP'
        ? 'PEP Exposure Match'
        : complianceRisk === 'WANTED'
          ? 'Wanted Entity Match'
          : 'Critical Sanctions Match';
    const points = complianceRisk === 'PEP' ? 35 : complianceRisk === 'WANTED' ? 45 : 50;
    currentScore -= points;
    penalties.push({ reason, points });
  }
  return { currentScore, penalties };
}

export function computeStats(realResults, loading, activeTab) {
  if (realResults.length === 0 && !loading) {
    return {
      score: '--',
      label: 'Pending Analysis',
      colorClass: 'border-slate-200 text-slate-400',
      breaches: 0,
      count: 0,
      penalties: [],
    };
  }
  const branch =
    activeTab === TAB_COMPANY ? computeCompanyTabScore(realResults) : computeNonCompanyTabScore(realResults);
  const currentScore = Math.max(branch.currentScore, 0);
  return statsResultBand(currentScore, realResults, branch.penalties);
}

export function createInitialTabStorage() {
  return {
    person: {
      input: '',
      results: [],
      logs: [],
      completed: false,
      searchScope: 'ro',
      inputError: '',
      locationTags: [],
      contextTags: [],
      usernameTags: [],
      scanType: 'light',
      extractedLeads: {
        emails: [],
        phones: [],
        cuis: [],
        associated_urls: [],
        associated_urls_partial: [],
        pivots: [],
      },
    },
    email: { input: '', results: [], logs: [], completed: false, inputError: '' },
    phone: {
      input: '',
      results: [],
      logs: [],
      completed: false,
      searchScope: 'ro',
      inputError: '',
      extractedLeads: {
        emails: [],
        phones: [],
        cuis: [],
        associated_urls: [],
        associated_urls_partial: [],
        pivots: [],
      },
    },
    company: {
      name: '',
      domain: '',
      country: 'GLOBAL',
      locationTags: [],
      results: [],
      logs: [],
      completed: false,
      inputError: '',
    },
  };
}

/** Delimiters: identity :: professional context (sent as context_focus). */
const PERSON_INLINE_FOCUS_SEPS = [' :: ', ' // ', ' > '];

export function mergePersonTagLists(existing, added) {
  const out = [];
  const seen = new Set();
  for (const list of [existing, added]) {
    for (const item of list || []) {
      const t = String(item || '').trim();
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
  }
  return out;
}

export function splitPersonInlineFocus(query) {
  const q = String(query || '').trim();
  if (!q) return { input: '', contextTags: [] };
  for (const sep of PERSON_INLINE_FOCUS_SEPS) {
    const idx = q.indexOf(sep);
    if (idx !== -1) {
      const focus = q.slice(idx + sep.length).trim();
      return { input: q.slice(0, idx).trim(), contextTags: focus ? [focus] : [] };
    }
  }
  return { input: q, contextTags: [] };
}

/** Apply hero query into tab storage for the chosen module. */
export function patchTabStorageForQuery(prev, tab, query) {
  const q = String(query || '').trim();
  const err = validateTabInputValue(tab, q);
  if (tab === TAB_COMPANY) {
    const parsed = parseCorporateHeroQuery(q);
    const err = validateTabInputValue(tab, parsed.companyName || q);
    const domainVal = parsed.domain || (companyFieldHintForQuery(q) === 'domain' ? normalizeCompanyDomainInput(q) : '');
    const country = domainVal ? inferCompanyCountryFromDomain(domainVal) : prev.company.country || 'GLOBAL';
    return {
      ...prev,
      company: {
        ...prev.company,
        name: parsed.streamName || parsed.companyName || q,
        domain: domainVal,
        country,
        locationTags: mergePersonTagLists(prev.company.locationTags, parsed.locationTags),
        inputError: err,
      },
    };
  }
  if (tab === TAB_PERSON) {
    const { input, contextTags } = splitPersonInlineFocus(q);
    return {
      ...prev,
      person: {
        ...prev.person,
        input,
        contextTags: mergePersonTagLists(prev.person.contextTags, contextTags),
        inputError: validateTabInputValue(tab, input),
      },
    };
  }
  return {
    ...prev,
    [tab]: { ...prev[tab], input: q, inputError: err },
  };
}

/** Prefer subscription management URL, then profile/homepage for registered services. */
export function subscriptionHrefForService(svc) {
  if (!svc || typeof svc !== 'object') return '#';
  const manage = String(svc.manage_url || '').trim();
  if (manage) return manage;
  const profile = String(svc.profile_url || svc.url || '').trim();
  if (profile && profile !== '#') return profile;
  const domain = String(svc.domain || '')
    .trim()
    .replace(/^https?:\/\//, '');
  return domain ? `https://${domain}` : '#';
}

/** Group registered services by account_family for subscription management UI. */
export function groupServicesByAccountFamily(services) {
  const grouped = new Map();
  const ungrouped = [];
  for (const svc of services || []) {
    const family = String(svc?.account_family || '').trim();
    if (!family) {
      ungrouped.push(svc);
      continue;
    }
    if (!grouped.has(family)) grouped.set(family, []);
    grouped.get(family).push(svc);
  }
  const sections = [...grouped.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([family, items]) => ({ family, services: items }));
  if (ungrouped.length) {
    sections.push({ family: null, services: ungrouped });
  }
  return sections;
}
